import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Compass, 
  Plane, 
  Building, 
  Clock, 
  Sparkles, 
  MapPin, 
  RefreshCw, 
  Printer, 
  UtensilsCrossed, 
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  Layers,
  HeartHandshake,
  Moon,
  Landmark,
  Wine,
  HelpCircle,
  Coffee,
  Heart,
  Gift
} from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';

export default function Guide() {
  const { lang } = useLang();
  const isEn = lang === 'en';
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0b1437] text-white px-4 py-8 md:px-12 flex flex-col items-center">
      <div className="max-w-4xl w-full">
        
        {/* Pulsante Indietro */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isEn ? "Back" : "Indietro"}</span>
          </button>
        </div>

        {/* Intestazione Principale */}
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold flex items-center justify-center gap-2 mb-2">
            <HelpCircle className="w-4 h-4 text-[#d4af37]" />
            <span>{isEn ? "Official User Manual • Holiday Genie" : "Manuale Utente Ufficiale • Il Genio in Vacanza"}</span>
          </span>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-[#ede7d8] mb-3">
            {isEn ? "How the Genie Crafts Your Journey" : "Come Funziona il Tuo Genio"}
          </h1>
          <p className="text-sm md:text-base text-gray-300 max-w-2xl mx-auto leading-relaxed">
            {isEn 
              ? "Discover how to plan seamless trips in under 2 minutes: logistical flight synchronization, tailored daytime pacing, curated authentic dining, and interactive day-by-day rework."
              : "Scopri come pianificare il viaggio ideale in meno di 2 minuti: calcolo automatico dei transfer aeroportuali, ritmi di viaggio su misura, ristoranti d'autore e ricalibrazione giorno per giorno."}
          </p>
        </div>

        {/* FLUSSO IN 4 PASSI */}
        <div className="space-y-8 mb-12">

          {/* PASSO 1 */}
          <div className="bg-[#12204f]/60 border border-gray-800 rounded-2xl p-6 md:p-8 shadow-xl relative overflow-hidden">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#d4af37] text-[#0b1437] font-bold flex items-center justify-center text-sm shadow">
                1
              </span>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#ede7d8]">
                {isEn ? "Choose Destination & Travel Dates" : "Scegli la Meta e le Date"}
              </h2>
            </div>

            <p className="text-xs md:text-sm text-gray-300 leading-relaxed mb-4">
              {isEn 
                ? "Type your departure city and target destination. The Genie instantly auto-resolves global airport IATA codes (e.g., LON, VCE, BCN, PAR, NCE) to ensure flawless route mapping."
                : "Inserisci la tua città di partenza e la destinazione desiderata. Il sistema riconosce in tempo reale gli aeroporti e i codici IATA corrispondenti per garantire collegamenti precisi."}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#0b1437]/70 p-4 rounded-xl border border-gray-700/60 text-xs text-gray-300">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#d4af37]" />
                <span><strong>{isEn ? "Departure City" : "Partenza"}:</strong> {isEn ? "e.g. London, Rome..." : "es. Venezia, Milano..."}</span>
              </div>
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-[#d4af37]" />
                <span><strong>{isEn ? "Destination" : "Meta"}:</strong> {isEn ? "e.g. Ibiza, Paris..." : "es. Barcellona, Cannes..."}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#d4af37]" />
                <span><strong>{isEn ? "Dates & Pax" : "Date & Persone"}:</strong> {isEn ? "Selected flexibly" : "Calendario con passeggeri"}</span>
              </div>
            </div>
          </div>

          {/* PASSO 2 */}
          <div className="bg-[#12204f]/60 border-2 border-[#d4af37]/60 rounded-2xl p-6 md:p-8 shadow-xl relative">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#d4af37] text-[#0b1437] font-bold flex items-center justify-center text-sm shadow">
                2
              </span>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#ede7d8]">
                {isEn ? "Lock Flights & Hotels at Partner Rates" : "Blocca Volo e Hotel ai Migliori Prezzi"}
              </h2>
            </div>

            <p className="text-xs md:text-sm text-gray-300 leading-relaxed mb-4">
              {isEn 
                ? "Before launching the AI generation, click the official partner buttons to compare over 700 airlines on Aviasales and find handpicked central accommodations on Hotellook with zero booking markups."
                : "Prima di generare l'itinerario, sfrutta i canali ufficiali integrati: con un clic verifichi le tariffe più vantaggiose su oltre 700 compagnie con Aviasales e individui la sistemazione ideale tramite Hotellook a costo zero."}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-[#1b2559] p-3.5 rounded-xl border border-gray-700 flex items-center gap-3">
                <Plane className="w-6 h-6 text-[#d4af37] flex-shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-white uppercase">{isEn ? "Flight Comparator" : "Comparatore Voli"}</h4>
                  <p className="text-[11px] text-gray-300 mt-0.5">{isEn ? "Synchronizes your exact route and dates directly" : "Apre la tratta e le date inserite con un clic"}</p>
                </div>
              </div>
              <div className="bg-[#1b2559] p-3.5 rounded-xl border border-gray-700 flex items-center gap-3">
                <Building className="w-6 h-6 text-[#d4af37] flex-shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-white uppercase">{isEn ? "Hotel Look" : "Ricerca Alloggio"}</h4>
                  <p className="text-[11px] text-gray-300 mt-0.5">{isEn ? "Locks in central rooms with direct cancellation policy checks" : "Stanze e b&b nelle zone migliori della città"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* PASSO 3 */}
          <div className="bg-[#12204f]/60 border border-gray-800 rounded-2xl p-6 md:p-8 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#d4af37] text-[#0b1437] font-bold flex items-center justify-center text-sm shadow">
                3
              </span>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#ede7d8]">
                {isEn ? "Flight Timings & Personalized Trip Style" : "Sincronizzazione Voli e Stile di Vacanza"}
              </h2>
            </div>

            <p className="text-xs md:text-sm text-gray-300 leading-relaxed mb-4">
              {isEn
                ? "This is where the Genie stands out from standard travel bots. It synchronizes arrival disembarkation buffers and tunes daytime rhythms based on your lifestyle:"
                : "Questo è il vero valore aggiunto del Genio rispetto ai banali elenchi di viaggio. Il sistema adatta il ritmo biologico alle tue preferenze e calcola i tempi reali di transfer:"}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-[#0b1437]/80 p-4 rounded-xl border border-gray-700">
                <div className="flex items-center gap-2 text-amber-300 font-semibold text-xs mb-1">
                  <Clock className="w-4 h-4" />
                  <span>{isEn ? "Smart Transfer Calculation" : "Calcolo Buffer Aeroporto"}</span>
                </div>
                <p className="text-[11px] text-gray-300 leading-relaxed">
                  {isEn 
                    ? "Calculates ~1h30m for baggage claim & transfer on Day 1, and plans airport departure ~2h30m before takeoff on the final day."
                    : "Calcola circa 1h30m per ritiro valigie e transfer all'andata, e imposta l'arrivo al terminal circa 2h30m prima del decollo al rientro."}
                </p>
              </div>

              <div className="bg-[#0b1437]/80 p-4 rounded-xl border border-gray-700">
                <div className="flex items-center gap-2 text-amber-300 font-semibold text-xs mb-1">
                  <UtensilsCrossed className="w-4 h-4" />
                  <span>{isEn ? "Authentic Dining Selection" : "Ristoranti d'Autore"}</span>
                </div>
                <p className="text-[11px] text-gray-300 leading-relaxed">
                  {isEn 
                    ? "Never outputs placeholders: always quotes real, verified, highly-rated restaurants (Budget, Balanced, or Luxury Michelin level)."
                    : "Zero segnaposto generici: solo locali reali e rinomati (da osterie veraci a trattorie tipiche o ristoranti fine dining)."}
                </p>
              </div>
            </div>

            {/* LE 5 CATEGORIE */}
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#d4af37] mb-3">
              {isEn ? "The 5 Experience Styles:" : "Le 5 Categorie di Viaggio:"}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              <div className="bg-[#0b1437]/90 p-3 rounded-xl border border-gray-700 text-center">
                <Compass className="w-4 h-4 text-[#d4af37] mx-auto mb-1" />
                <span className="text-xs font-bold text-white block">Mix</span>
                <span className="text-[10px] text-gray-400">{isEn ? "Balanced harmony" : "Cultura, relax e cene"}</span>
              </div>
              <div className="bg-[#0b1437]/90 p-3 rounded-xl border border-gray-700 text-center">
                <Landmark className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                <span className="text-xs font-bold text-white block">{isEn ? "Culture" : "Arte & Musei"}</span>
                <span className="text-[10px] text-gray-400">{isEn ? "Early morning start" : "Visite e monumenti"}</span>
              </div>
              <div className="bg-[#0b1437]/90 p-3 rounded-xl border border-gray-700 text-center">
                <Moon className="w-4 h-4 text-pink-400 mx-auto mb-1" />
                <span className="text-xs font-bold text-white block">{isEn ? "Nightlife" : "Movida & Notte"}</span>
                <span className="text-[10px] text-gray-400">{isEn ? "Clubs & late brunch" : "Rooftop, club e risveglio tardivo"}</span>
              </div>
              <div className="bg-[#0b1437]/90 p-3 rounded-xl border border-gray-700 text-center">
                <HeartHandshake className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                <span className="text-xs font-bold text-white block">{isEn ? "Spa & Wellness" : "Benessere & Spa"}</span>
                <span className="text-[10px] text-gray-400">{isEn ? "Baths & slow rhythm" : "Terme, relax e ritmi lenti"}</span>
              </div>
              <div className="bg-[#0b1437]/90 p-3 rounded-xl border border-gray-700 text-center">
                <Wine className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                <span className="text-xs font-bold text-white block">{isEn ? "Food & Wine" : "Gusto & Tradizione"}</span>
                <span className="text-[10px] text-gray-400">{isEn ? "Tastings & markets" : "Mercati e degustazioni"}</span>
              </div>
            </div>
          </div>

          {/* PASSO 4 */}
          <div className="bg-[#12204f]/60 border border-gray-800 rounded-2xl p-6 md:p-8 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#d4af37] text-[#0b1437] font-bold flex items-center justify-center text-sm shadow">
                4
              </span>
              <h2 className="text-xl md:text-2xl font-serif font-bold text-[#ede7d8]">
                {isEn ? "Live, Navigate & Rework Day-by-Day" : "Naviga, Stampa e Ricalibra la Giornata"}
              </h2>
            </div>

            <p className="text-xs md:text-sm text-gray-300 leading-relaxed mb-4">
              {isEn 
                ? "Your itinerary is ready to accompany your trip with real-time navigation and full interactive control:"
                : "L'itinerario salvato non è una pagina statica ma una cabina di regia dinamica a tua completa disposizione:"}
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-[#0b1437]/70 p-3.5 rounded-xl border border-gray-700/60">
                <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-white">{isEn ? "One-Click Maps & Menus" : "Mappe e Menù in un Clic"}</h4>
                  <p className="text-[11px] text-gray-300 mt-0.5">
                    {isEn 
                      ? "Every single restaurant and point of interest links directly to Google Maps for directions, reviews, and reservations." 
                      : "Ogni ristorante e attività presenta un pulsante diretto per aprire la posizione esatta su Google Maps, visionare foto e prenotare."}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-[#0b1437]/70 p-3.5 rounded-xl border border-[#d4af37]/40">
                <RefreshCw className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-[#d4af37]">{isEn ? "Exclusive 'Rework Day' Button" : "Tasto Esclusivo 'Ricalibra Giorno'"}</h4>
                  <p className="text-[11px] text-gray-300 mt-0.5">
                    {isEn 
                      ? "Want a wild party night on Day 2 instead of museums? Select 'Nightlife' above Day 2 and hit 'Rework Day': only that specific day is instantly rewritten in 5 seconds." 
                      : "Hai deciso che il Giorno 2 preferisci fare movida o dedicarti alla Spa invece di visitare musei? Clicca il tema desiderato sopra la giornata e premi 'Ricalibra': in 5 secondi solo quel giorno viene riscritto su misura."}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-[#0b1437]/70 p-3.5 rounded-xl border border-gray-700/60">
                <Printer className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-white">{isEn ? "Print & Offline PDF" : "Stampa & PDF da Portare con Te"}</h4>
                  <p className="text-[11px] text-gray-300 mt-0.5">
                    {isEn 
                      ? "Easily print or export clean PDF summaries to keep on your smartphone even when traveling offline." 
                      : "Puoi stampare la pagina o esportare il PDF pulito da consultare sullo smartphone anche all'estero senza connessione."}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SEZIONE SPECIALE: 100% GRATIS & SUPPORTO AL GENIO */}
          <div className="bg-gradient-to-br from-[#152347] to-[#12204f] border-2 border-[#d4af37]/70 rounded-2xl p-6 md:p-8 shadow-2xl relative overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2.5">
                <Gift className="w-6 h-6 text-[#d4af37]" />
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#ede7d8]">
                  {isEn ? "100% Free: Support the Project" : "Un Servizio 100% Gratuito: Sostieni il Genio!"}
                </h2>
              </div>
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1 bg-emerald-950/80 border border-emerald-500/50 text-emerald-300 rounded-full font-semibold self-start sm:self-center">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                {isEn ? "Zero Subscription / Zero Fees" : "Zero Abbonamenti / Zero Costi"}
              </span>
            </div>

            <p className="text-xs md:text-sm text-gray-200 leading-relaxed mb-4">
              {isEn 
                ? "Holiday Genie is completely free: you can create, modify, download, and print unlimited itineraries without ever entering a credit card. Behind every calculated transfer and verified venue lies hours of passionate development."
                : "Il Genio in Vacanza è una piattaforma totalmente gratuita: puoi creare, salvare, ricalibrare, scaricare in PDF e stampare tutti gli itinerari che vuoi senza dover sottoscrivere alcun abbonamento o inserire dati di pagamento. Dietro ogni transfer calcolato al minuto e ogni locale suggerito ci sono ore di dedizione e sviluppo continuo."}
            </p>

            <div className="bg-[#0b1437]/70 p-4 rounded-xl border border-[#d4af37]/30 mb-6">
              <div className="flex items-center gap-2 text-[#d4af37] font-semibold text-xs mb-1.5">
                <Heart className="w-4 h-4 fill-[#d4af37]" />
                <span>{isEn ? "Enjoyed your itinerary? Reward the work done!" : "Ti è piaciuto il viaggio generato? Premia il lavoro svolto!"}</span>
              </div>
              <p className="text-[11px] md:text-xs text-gray-300 leading-relaxed">
                {isEn 
                  ? "If the Genie saved you hours of research and helped you travel smarter, you can leave a small symbolic tip to keep the servers running and reward our work. You'll find the donation box right at the bottom of each generated trip:"
                  : "Se il Genio ti ha fatto risparmiare ore di ricerche e ti ha regalato un itinerario perfetto, è sempre ben gradita una piccola offerta simbolica per coprire i costi dei server AI e premiare il nostro impegno. In fondo a ogni itinerario generato trovi le 3 opzioni rapide:"}
              </p>
            </div>

            {/* LE 3 PROPOSTE SIMBOLICHE */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <a 
                href="https://paypal.me/omarferro63/2" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center justify-center gap-2.5 p-3.5 bg-[#795548]/30 hover:bg-[#795548]/60 border border-[#795548]/60 rounded-xl text-xs font-semibold text-amber-200 transition shadow"
              >
                <span className="text-lg">☕</span>
                <span>{isEn ? "Buy me a coffee (2 EUR)" : "Offrimi un caffè (2 EUR)"}</span>
              </a>

              <a 
                href="https://paypal.me/omarferro63/5" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center justify-center gap-2.5 p-3.5 bg-[#d4af37]/20 hover:bg-[#d4af37]/40 border border-[#d4af37]/60 rounded-xl text-xs font-semibold text-[#d4af37] transition shadow"
              >
                <span className="text-lg">🥐</span>
                <span>{isEn ? "Treat me to breakfast (5 EUR)" : "Offrimi la colazione (5 EUR)"}</span>
              </a>

              <a 
                href="https://paypal.me/omarferro63/10" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center justify-center gap-2.5 p-3.5 bg-[#e53935]/20 hover:bg-[#e53935]/40 border border-[#e53935]/60 rounded-xl text-xs font-semibold text-red-300 transition shadow"
              >
                <span className="text-lg">🍕</span>
                <span>{isEn ? "Buy me a pizza (10 EUR)" : "Offrimi una pizza (10 EUR)"}</span>
              </a>
            </div>
          </div>

        </div>

        {/* CTA FINALE */}
        <div className="bg-[#12204f] border-2 border-[#d4af37] rounded-2xl p-6 md:p-8 text-center shadow-2xl mb-12">
          <Sparkles className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
          <h3 className="text-2xl font-serif font-bold text-[#ede7d8] mb-2">
            {isEn ? "Ready to create your next memory?" : "Pronto a creare il tuo prossimo viaggio?"}
          </h3>
          <p className="text-xs md:text-sm text-gray-300 max-w-md mx-auto mb-6">
            {isEn 
              ? "Put the Genie to work now and start packing your bags!" 
              : "Metti subito alla prova il Genio e comincia a preparare le valigie!"}
          </p>
          <Link
            to="/plan"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#d4af37] hover:bg-[#b8962d] text-[#0b1437] font-bold rounded-xl text-sm transition shadow-lg"
          >
            <span>{isEn ? "Start Planning Now" : "Inizia a Pianificare Ora"}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

      </div>
    </div>
  );
}
